import React, { useEffect, useState } from "react";
import { Col, Dropdown, Input, DatePicker } from "antd";
import { CalendarOutlined, DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { getPresetRange } from "../../../hooks/useDateRangePresets";
import {
  currentDateStartTime,
  currentDateEndTime,
  toCompanyDateOnlyforReportDateFilter,
} from "../../../lib/functions";
import "./commonDateRangeFilter.css";

const { RangePicker } = DatePicker;

const QUICK_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "custom", label: "Custom Range" },
];

const CommonDateRangeFilter = ({ filters, onChange }) => {
  const [open, setOpen] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [showFormatHint, setShowFormatHint] = useState(false);
  const inputRef = React.useRef(null);

  const isMobile = window.innerWidth < 768;


  /* Detect active quick range */
  useEffect(() => {
    if (!filters.fromDate || !filters.toDate) return;

    for (const r of QUICK_RANGES) {
      if (r.key === "custom") continue;
      const preset = getPresetRange(r.key);
      if (
        preset &&
        dayjs(toCompanyDateOnlyforReportDateFilter(filters.fromDate)).isSame(preset.fromDate) &&
        dayjs(toCompanyDateOnlyforReportDateFilter(filters.toDate)).isSame(preset.toDate)
      ) {
        setActiveKey(r.key);
        return;
      }
    }
    setActiveKey("custom");
  }, [filters.fromDate, filters.toDate]);

  // Sync manually typed input  when filters change:
  useEffect(() => {
  if (filters.fromDate && filters.toDate) {
    setInputValue(
      `${toCompanyDateOnlyforReportDateFilter(filters.fromDate).format("DD/MM/YYYY")} → ${toCompanyDateOnlyforReportDateFilter(
        filters.toDate
      ).format("DD/MM/YYYY")}`
    );
  } else {
    setInputValue("");
  }
}, [filters.fromDate, filters.toDate]);

  /* Handle quick range click */
  const handleMenuClick = ({ key }) => {
  setActiveKey(key);

  if (key === "custom") {
    setOpen(false);

    // ⏱ allow dropdown to close first
    setTimeout(() => {
      setOpenCalendar(true);
    }, 0);

    return;
  }

  const range = getPresetRange(key);
  if (!range) return;

  onChange(range.fromDate, range.toDate);
  setOpen(false);
};

// Handle parse manually typed input safely
const handleManualInput = () => {
  if (!inputValue) return;

  // Normalize separators
  const cleaned = inputValue
    .replace(/to/gi, "→")
    .replace(/-/g, "/");

  const parts = cleaned.split("→").map((p) => p.trim());

  if (parts.length !== 2) {
    Swal.fire("Invalid format", "Use DD/MM/YYYY → DD/MM/YYYY", "warning");
    return;
  }

  const start = dayjs(parts[0], "DD/MM/YYYY", true);
  const end = dayjs(parts[1], "DD/MM/YYYY", true);

  if (!start.isValid() || !end.isValid()) {
    Swal.fire("Invalid date", "Please enter valid dates", "warning");
    return;
  }

  const fromDate = currentDateStartTime(start);
  const toDate = currentDateEndTime(end);

  if (dayjs(fromDate).isAfter(dayjs(toDate))) {
    Swal.fire(
      "Invalid range",
      "From Date cannot be after To Date",
      "warning"
    );
    return;
  }

  onChange(fromDate, toDate);
  setActiveKey("custom");
  setShowFormatHint(false);
};


// manual typing to auto format
const formatDateRangeInput = (value) => {
  // Allow digits, slash, arrow, space
  let v = value.replace(/[^\d/→\s]/g, "");

  // Split by arrow if already present
  const parts = v.split("→").map(p => p.trim());

  const formatDate = (str) => {
    const digits = str.replace(/\D/g, "").slice(0, 8);

    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  if (parts.length === 1) {
    return formatDate(parts[0]);
  }

  if (parts.length >= 2) {
    return `${formatDate(parts[0])} → ${formatDate(parts[1])}`;
  }

  return v;
};
// Mask + caret-safe formatter
const MASK = "DD/MM/YYYY → DD/MM/YYYY";

const formatWithCaret = (raw, caretPos) => {
  // Extract only digits
  const digits = raw.replace(/\D/g, "");

  // Split into two fixed chunks
  const d1 = digits.slice(0, 2);
  const m1 = digits.slice(2, 4);
  const y1 = digits.slice(4, 8);

  const d2 = digits.slice(8, 10);
  const m2 = digits.slice(10, 12);
  const y2 = digits.slice(12, 16);

  let formatted = "";

  if (digits.length > 0) {
    formatted += d1;
    if (digits.length > 2) formatted += "/" + m1;
    if (digits.length > 4) formatted += "/" + y1;
  }

  // Auto insert arrow only after first full date
  if (digits.length >= 8) {
    formatted += " → ";
  }

  if (digits.length > 8) {
    formatted += d2;
    if (digits.length > 10) formatted += "/" + m2;
    if (digits.length > 12) formatted += "/" + y2;
  }

  // Caret handling (simple & stable)
  let newCaret = formatted.length;

  return { formatted, caret: newCaret };
};


  /* Calendar selection */
  const handleRangeChange = (values) => {
    if (!values) return;

    setShowFormatHint(false);

    const [start, end] = values;
    const fromDate = currentDateStartTime(start);
    const toDate = currentDateEndTime(end);

    if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date Range",
        text: "From Date cannot be after To Date",
        confirmButtonColor: "#1677ff",
      });
      return;
    }

    onChange(fromDate, toDate);
    setActiveKey("custom");
    setOpenCalendar(false);  
  };

  /* AntD menu */
  const menuItems = QUICK_RANGES.map((r) => ({
    key: r.key,
    label: r.label,
    className: activeKey === r.key ? "antd-range-active" : "",
  }));

  return (
    <Col xs={24} sm={12} md={12} lg={8} xl={6}>
      <label className="form-label-nowrap">
        <span style={{ color: "#ff4d4f" }}>*</span> Date Range
      </label>

      <Dropdown
        menu={{ items: menuItems, onClick: handleMenuClick }}
        trigger={["click"]}
        open={open}
        onOpenChange={setOpen}
        className="DateRangeFilter"
        placement="bottomLeft"
      >
        <div
          onClick={() => setOpen(true)}
          style={{ cursor: "pointer" }}
        >
        <Input
          ref={inputRef}
          prefix={<CalendarOutlined />}
          suffix={
          <span className="date-range-down-icon">
            <DownOutlined />
          </span>}
          data-testid="dateRangePicker"
          value={inputValue}
          onChange={(e) => {
            const input = e.target;
            const raw = input.value;
            const caretPos = input.selectionStart;

            const { formatted, caret } = formatWithCaret(raw, caretPos);

            setInputValue(formatted);

            // 👇 show hint only when user types something
            setShowFormatHint(formatted.length > 0);

            requestAnimationFrame(() => {
              if (inputRef.current) {
                inputRef.current.setSelectionRange(caret, caret);
              }
            });
          }}

          onBlur={handleManualInput}
          onPressEnter={handleManualInput}
          placeholder="DD/MM/YYYY → DD/MM/YYYY"
        />
      {showFormatHint && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
          <InfoCircleOutlined style={{ fontSize: 12, color: "#8c8c8c" }} />
          <span style={{ fontSize: 12, color: "#8c8c8c" }}>
            Type dates as DD/MM/YYYY → DD/MM/YYYY
          </span>
        </div>
      )}

        </div>
      </Dropdown>

      {/* Hidden calendar */}
      <div
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: openCalendar ? "auto" : "none",
            opacity: 0
        }}
        >
        <RangePicker
            open={openCalendar}
            panelRender={(panel) => panel}
            classNames={{
              popup: {
                root: 'mobile-range-picker',
              },
            }}
            value={
              filters.fromDate && filters.toDate
                ? [
                    toCompanyDateOnlyforReportDateFilter(filters.fromDate),
                    toCompanyDateOnlyforReportDateFilter(filters.toDate),
                  ]
                : null
            }
            onChange={handleRangeChange}
            onOpenChange={(next) => {
            if (!next) setOpenCalendar(false);
            }}
            getPopupContainer={() => document.body}
            className="hidden-range-picker"
        />
        </div>



    </Col>
  );
};

export default CommonDateRangeFilter;
